import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Modal, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap'
import {
  FaAngleDoubleDown,
  FaAngleDoubleUp,
  FaChevronRight,
  FaEraser,
  FaSearch,
} from 'react-icons/fa'
import { bookOpenIcon } from '../../constants/icons'
import { getSubjectDetailsById } from '../../api/user/subject.api'
import {
  allPickerLeavesSelected,
  buildPickerTreeFromSubjectDetail,
  buildPickerTreeFromTopicList,
  collectAllPickerNodeIds,
  collectPickerLeafIds,
  collectSubtreeNodeIds,
  countPickerSelectedLeaves,
  EXAM_SYLLABUS_LEVELS,
  getPickerTreeCheckState,
  levelAriaLabel,
  pickPrimaryTopicIdFromSelection,
  somePickerLeafSelected,
} from '../../utils/subjectSyllabusTree'
import {
  buildDefaultExpanded,
  buildExpandMap,
  expandAllInTree,
  filterTreeByLabel,
} from '../../utils/syllabusTreeUtils'

function PickerTreeItem({
  node,
  depth,
  expanded,
  onToggleExpand,
  selectedChapters,
  onToggleCheck,
  isTopLevel,
}) {
  const inputRef = useRef(null)
  const hasChildren = Boolean(node.children?.length)
  const isOpen = expanded[node.id]
  const checkState = getPickerTreeCheckState(node, selectedChapters)
  const level = levelAriaLabel(depth, EXAM_SYLLABUS_LEVELS)
  const inputId = `syllabus-picker-node-${node.id}`

  useEffect(() => {
    const el = inputRef.current
    if (el) el.indeterminate = checkState === 'indeterminate'
  }, [checkState])

  const rowClass = [
    'subject-syllabus-picker__row',
    hasChildren
      ? 'subject-syllabus-picker__row--branch'
      : 'subject-syllabus-picker__row--leaf',
    checkState === 'checked' ? 'subject-syllabus-picker__row--selected' : '',
    checkState === 'indeterminate'
      ? 'subject-syllabus-picker__row--mixed'
      : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <li
      role='none'
      className={`subject-syllabus-picker__branch${isTopLevel ? ' subject-syllabus-picker__branch--root' : ''}`}
    >
      <div
        className={rowClass}
        style={{ '--depth': depth }}
        data-depth={depth}
        role='treeitem'
        aria-level={depth + 1}
        aria-expanded={hasChildren ? isOpen : undefined}
        aria-checked={
          checkState === 'indeterminate'
            ? 'mixed'
            : checkState === 'checked'
        }
      >
        <div className='subject-syllabus-picker__row-track' />
        <div className='subject-syllabus-picker__row-body'>
          {hasChildren ? (
            <button
              type='button'
              className={`subject-syllabus-picker__disclose${isOpen ? ' is-open' : ''}`}
              onClick={(e) => {
                e.stopPropagation()
                onToggleExpand(node.id)
              }}
              aria-label={isOpen ? `Collapse ${node.label}` : `Expand ${node.label}`}
            >
              <FaChevronRight aria-hidden className='subject-syllabus-picker__disclose-ico' />
            </button>
          ) : (
            <span
              className='subject-syllabus-picker__disclose subject-syllabus-picker__disclose--spacer'
              aria-hidden
            />
          )}
          <input
            ref={inputRef}
            type='checkbox'
            className='subject-syllabus-picker__checkbox'
            id={inputId}
            checked={checkState === 'checked'}
            onChange={() => onToggleCheck(node)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`${level}: ${node.label}`}
          />
          <label className='subject-syllabus-picker__label' htmlFor={inputId}>
            {node.label}
            <span className='visually-hidden'>{` (${level}${hasChildren ? ', includes nested units' : ''})`}</span>
          </label>
        </div>
      </div>
      {hasChildren && isOpen ? (
        <ul className='subject-syllabus-picker__nest' role='group'>
          {node.children.map((child) => (
            <PickerTreeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              selectedChapters={selectedChapters}
              onToggleCheck={onToggleCheck}
              isTopLevel={false}
            />
          ))}
        </ul>
      ) : null}
    </li>
  )
}

function SubjectSyllabusPickerModal({
  show,
  onHide,
  subjectId,
  subjectName,
  fallbackTopics = [],
  onConfirm,
}) {
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [detailError, setDetailError] = useState(null)
  const [treeData, setTreeData] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [expanded, setExpanded] = useState({})
  const [selectedChapters, setSelectedChapters] = useState({})

  const allIds = useMemo(() => collectAllPickerNodeIds(treeData), [treeData])
  const totalLeaves = useMemo(() => collectPickerLeafIds(treeData).length, [treeData])
  const leafCount = countPickerSelectedLeaves(treeData, selectedChapters)
  const primaryTopicId = pickPrimaryTopicIdFromSelection(
    selectedChapters,
    treeData,
  )

  const loadDetail = useCallback(async () => {
    if (!subjectId) {
      setLoadingDetail(false)
      return
    }
    setLoadingDetail(true)
    setDetailError(null)
    try {
      const detail = await getSubjectDetailsById(subjectId)
      const fromDetail = buildPickerTreeFromSubjectDetail(detail)
      const nextTree =
        fromDetail.length > 0
          ? fromDetail
          : buildPickerTreeFromTopicList(fallbackTopics)
      setTreeData(nextTree)
      setExpanded(buildDefaultExpanded(nextTree, 2))
    } catch (e) {
      console.error('Subject syllabus picker: failed to load subject details', e)
      setDetailError(
        e?.response?.data?.message || e?.message || 'Could not load details.',
      )
      const nextTree = buildPickerTreeFromTopicList(fallbackTopics)
      setTreeData(nextTree)
      setExpanded(buildDefaultExpanded(nextTree, 2))
    } finally {
      setLoadingDetail(false)
    }
  }, [subjectId, fallbackTopics])

  useEffect(() => {
    if (!show) return
    setSearchQuery('')
    setSelectedChapters({})
    setTreeData([])
    setDetailError(null)
    setExpanded({})
    loadDetail()
  }, [show, loadDetail])

  const visibleTree = useMemo(
    () => filterTreeByLabel(treeData, searchQuery),
    [treeData, searchQuery],
  )

  useEffect(() => {
    if (!treeData.length) return
    if (!searchQuery.trim()) {
      setExpanded(buildDefaultExpanded(treeData, 2))
    } else {
      setExpanded((prev) => ({
        ...prev,
        ...expandAllInTree(visibleTree, {}),
      }))
    }
  }, [searchQuery, treeData, visibleTree])

  const onToggleExpand = useCallback((id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const onToggleCheck = useCallback((node) => {
    const subtree = collectSubtreeNodeIds(node)
    const state = getPickerTreeCheckState(node, selectedChapters)
    const turnOn = state !== 'checked'
    setSelectedChapters((prev) => {
      const next = { ...prev }
      subtree.forEach((id) => {
        next[id] = turnOn
      })
      return next
    })
  }, [selectedChapters])

  const expandAllBranches = () => {
    setExpanded(buildExpandMap(treeData, true))
  }

  const collapseAllBranches = () => {
    setExpanded(buildExpandMap(treeData, false))
  }

  const toggleSelectAllInTree = () => {
    const turnOn = !allSel
    setSelectedChapters((prev) => {
      const next = { ...prev }
      allIds.forEach((id) => {
        next[id] = turnOn
      })
      return next
    })
  }

  const clearTreeSelection = () => {
    setSelectedChapters((prev) => {
      const next = { ...prev }
      allIds.forEach((id) => {
        next[id] = false
      })
      return next
    })
  }

  const pathSummary = useMemo(() => {
    const walk = (nodes, acc) => {
      for (const n of nodes || []) {
        const next = [...acc, n.label]
        if (!n.children?.length && selectedChapters[n.id]) return next
        if (n.children?.length) {
          const found = walk(n.children, next)
          if (found) return found
        }
      }
      return null
    }
    return walk(treeData, [])
  }, [selectedChapters, treeData])

  const handleConfirm = () => {
    if (!primaryTopicId || leafCount < 1) return
    onConfirm({
      topicId: primaryTopicId,
      pickerSelectedChapters: { ...selectedChapters },
      pathLabels: pathSummary || undefined,
    })
  }

  const canContinue =
    Boolean(primaryTopicId) &&
    leafCount > 0 &&
    somePickerLeafSelected(treeData, selectedChapters)
  const parentCheckboxRef = useRef(null)
  const allSel = allPickerLeavesSelected(treeData, selectedChapters)
  const someSel = somePickerLeafSelected(treeData, selectedChapters) && !allSel

  useEffect(() => {
    const el = parentCheckboxRef.current
    if (el) el.indeterminate = someSel
  }, [someSel])

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      scrollable
      className='subject-syllabus-picker common-modal'
      contentClassName='border-0'
    >
      <Modal.Header closeButton className='subject-syllabus-picker__header'>
        <Modal.Title as='div' className='subject-syllabus-picker__title-wrap'>
          <div className='subject-syllabus-picker__headline'>
            <span className='subject-syllabus-picker__headline-icon' aria-hidden>
              {bookOpenIcon}
            </span>
            <span className='subject-syllabus-picker__headline-text'>{subjectName}</span>
          </div>
          <p className='subject-syllabus-picker__meta'>
            {EXAM_SYLLABUS_LEVELS.join(' → ')}
          </p>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='p-0'>
        {loadingDetail ? (
          <div className='subject-syllabus-picker__loading' role='status' aria-live='polite'>
            <Spinner animation='border' size='sm' className='subject-syllabus-picker__spinner' />
            <span>Loading your syllabus…</span>
          </div>
        ) : (
          <>
            {detailError ? (
              <div className='subject-syllabus-picker__error-banner' role='alert'>
                {detailError}
              </div>
            ) : null}
            <div className='syllabus-panel multiple-checkbox subject-syllabus-picker__panel'>
              <div className='subject-syllabus-picker__picker-meta mx-3 mt-2'>
                <span className='subject-syllabus-picker__picker-count' aria-live='polite'>
                  {leafCount} / {totalLeaves} units selected
                </span>
              </div>
              <div className='syllabus-panel__search subject-syllabus-picker__search mx-3 mt-3'>
                <FaSearch className='syllabus-panel__search-icon' aria-hidden />
                <label htmlFor='syllabus-picker-search' className='visually-hidden'>
                  Search units
                </label>
                <input
                  id='syllabus-picker-search'
                  type='search'
                  className='syllabus-panel__search-input'
                  placeholder='Search units'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoComplete='off'
                />
                {searchQuery ? (
                  <button
                    type='button'
                    className='syllabus-panel__search-clear'
                    onClick={() => setSearchQuery('')}
                    aria-label='Clear search'
                  >
                    ×
                  </button>
                ) : null}
              </div>
              <div className='syllabus-panel__bulk'>
                <div className='syllabus-panel__bulk-main'>
                  <div className='form-check custom-checkbox syllabus-panel__bulk-check'>
                    <input
                      ref={parentCheckboxRef}
                      id='syllabus-picker-select-all'
                      type='checkbox'
                      className='form-check-input'
                      checked={allSel}
                      onChange={toggleSelectAllInTree}
                    />
                    <label className='form-check-label mb-0' htmlFor='syllabus-picker-select-all'>
                      {allSel
                        ? 'All units selected'
                        : someSel
                          ? 'Some units selected'
                          : 'Select all units'}
                    </label>
                  </div>
                </div>
                <div
                  className='syllabus-panel__bulk-actions'
                  role='group'
                  aria-label='Tree view actions'
                >
                  <OverlayTrigger
                    placement='top'
                    overlay={<Tooltip id='picker-tip-expand'>Show all nested topics</Tooltip>}
                  >
                    <button
                      type='button'
                      className='syllabus-panel__icon-btn'
                      onClick={expandAllBranches}
                      aria-label='Expand all branches'
                    >
                      <FaAngleDoubleDown aria-hidden className='syllabus-panel__icon-btn-ico' />
                    </button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement='top'
                    overlay={<Tooltip id='picker-tip-collapse'>Hide nested topics</Tooltip>}
                  >
                    <button
                      type='button'
                      className='syllabus-panel__icon-btn'
                      onClick={collapseAllBranches}
                      aria-label='Collapse all branches'
                    >
                      <FaAngleDoubleUp aria-hidden className='syllabus-panel__icon-btn-ico' />
                    </button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    placement='top'
                    overlay={<Tooltip id='picker-tip-clear'>Clear selection</Tooltip>}
                  >
                    <button
                      type='button'
                      className='syllabus-panel__icon-btn syllabus-panel__icon-btn--danger'
                      onClick={clearTreeSelection}
                      aria-label='Clear selection'
                      disabled={!treeData.length || leafCount < 1}
                    >
                      <FaEraser aria-hidden className='syllabus-panel__icon-btn-ico' />
                    </button>
                  </OverlayTrigger>
                </div>
              </div>
              <div className='subject-syllabus-picker__tree-wrap syllabus-panel__scroller px-2 pb-2'>
                {visibleTree.length === 0 ? (
                  <p className='subject-syllabus-picker__empty' role='status'>
                    {searchQuery.trim()
                      ? `No results for “${searchQuery.trim()}”. Try another word.`
                      : 'No syllabus units were returned for this subject.'}
                  </p>
                ) : (
                  <ul
                    className='subject-syllabus-picker__tree'
                    role='tree'
                    aria-label='Syllabus units'
                  >
                    {visibleTree.map((node) => (
                      <PickerTreeItem
                        key={node.id}
                        node={node}
                        depth={0}
                        expanded={expanded}
                        onToggleExpand={onToggleExpand}
                        selectedChapters={selectedChapters}
                        onToggleCheck={onToggleCheck}
                        isTopLevel
                      />
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className='subject-syllabus-picker__footer'>
        <div className='subject-syllabus-picker__footer-actions'>
          <Button variant='outline-secondary' onClick={onHide} className='btn-brown'>
            Cancel
          </Button>
          <Button
            variant='primary'
            className='filter-apply-syllabus subject-syllabus-picker__continue'
            disabled={!canContinue || loadingDetail}
            onClick={handleConfirm}
          >
            Continue
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default SubjectSyllabusPickerModal
